// React hooks
import { memo, useCallback, useEffect, useMemo, useState } from "react";

// Helpers i API servisi
import { useTranslate } from "helpers";
import { validateYup } from "services/forms/validation/validation";
import {
  useHandleDateChange,
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import {
  createFleetVehicleExpenseAPI,
  getFleetVehicleExpenseAPI,
  updateFleetVehicleExpenseAPI,
} from "services/api/fleet/vehicles/expenses/api";
import { transformFleetVehicleExpenseEditFormValues } from "services/api/fleet/vehicles/expenses/transformations";

// Tipovi i konstante
import { Errors } from "services/forms/validation/types";
import { FleetVehicleExpenseEditFormValues } from "services/api/fleet/vehicles/expenses/types";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";
import { initialFleetVehicleExpenseEditForm } from "services/api/fleet/vehicles/expenses/consts";
import { VehicleExpenseEditProps } from "./types";
import { FLEET_VEHICLE_EXPENSE_EDIT_FORM_SCHEMA } from "./validation";

// UI komponente
import { ExpenseAppliesTo } from "services/api/finance/expense-types/types";
import { ExpenseTypeDropdown } from "ui/components/Dropdown/ExpenseTypeDropdown";
import { DatePicker } from "ui/components/DatePicker";
import FileUploadControl from "ui/components/FileUpload/FileUploadControl/FileUploadControl";
import { Loader } from "ui/components/Loader";
import { Modal, ModalIntro } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { NumberField } from "ui/components/TextField/NumberField";
import { TextArea } from "ui/components/TextField/TextArea";
import { TextField } from "ui/components/TextField/TextField";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import WizardStep from "ui/components/Wizard/WizardStep";
import invoiceAnimation from "ui/theme/animations/invoice.json";
import {
  FLEET_VEHICLE_EXPENSE_CREATE_PAGE_TITLE,
  FLEET_VEHICLE_EXPENSE_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

const PDV = 0.25;
const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Unos i uređivanje troška vozila. Vrsta troška dolazi iz zajedničkog šifrarnika i bira se
 * dropdownom u samom obrascu — nema zasebnog koraka.
 */
const VehicleExpenseEditCard: React.FC<VehicleExpenseEditProps> = (props) => {
  const {
    show,
    vehicleId,
    expenseId,
    onSuccess,
    onClose,
    defaultExpenseTypeId,
    isFullScreen = true,
  } = props;

  const { t } = useTranslate();

  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stepCurrent, setStepCurrent] = useState<number>(0);

  const [formValues, setFormValues] = useState<FleetVehicleExpenseEditFormValues>(
    initialFleetVehicleExpenseEditForm
  );

  const [uploads, setUploads] = useState<FleetDocumentUpload[]>([]);

  useEffect(() => {
    if (!show) return;

    // Novi trošak može doći s predodabranom vrstom (npr. iz premještaja vozila).
    setFormValues({
      ...initialFleetVehicleExpenseEditForm,
      expenseTypeId: expenseId ? "" : defaultExpenseTypeId ?? "",
    });
    setUploads([]);
    setErrors({});
    setStepCurrent(0);

    if (expenseId) {
      fetchExpense(expenseId);
    }
  }, [show, expenseId, defaultExpenseTypeId]);

  const fetchExpense = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleExpenseEditForm);
      setUploads([]);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleExpenseAPI(id);
      if (result) {
        setFormValues(transformFleetVehicleExpenseEditFormValues(result));
        if (result.uploads && Array.isArray(result.uploads)) {
          setUploads(result.uploads);
        }
      }
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);

    setErrors({});
  };

  const handleAmountOrTaxChange = useCallback(
    (name: "amount" | "taxAmount", value: number | null) => {
      setFormValues((prev) => {
        if (value === null || isNaN(value)) {
          return {
            ...prev,
            [name]: null,
          };
        }

        if (name === "amount") {
          const taxAmount = round2(value * PDV);
          return {
            ...prev,
            amount: value,
            taxAmount,
          };
        }

        if (name === "taxAmount") {
          const amount = round2(value / PDV);
          return {
            ...prev,
            taxAmount: value,
            amount,
          };
        }

        return prev;
      });
    },
    []
  );

  const handleAmountChange = useCallback(
    (value: number | null) => {
      handleAmountOrTaxChange("amount", value);
    },
    [handleAmountOrTaxChange]
  );

  const handleTaxAmountChange = useCallback(
    (value: number | null) => {
      handleAmountOrTaxChange("taxAmount", value);
    },
    [handleAmountOrTaxChange]
  );

  const handleFormSubmit = useCallback(async () => {
    const isValid = validateYup(
      formValues,
      FLEET_VEHICLE_EXPENSE_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) return;

    setIsLoading(true);

    try {
      let result;

      if (expenseId) {
        result = await updateFleetVehicleExpenseAPI(
          vehicleId,
          expenseId,
          formValues,
          uploads,
          t
        );
      } else {
        result = await createFleetVehicleExpenseAPI(
          vehicleId,
          formValues,
          uploads,
          t
        );
      }

      if (result && onSuccess) onSuccess(result);
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  }, [formValues, expenseId, vehicleId, uploads, t, onSuccess]);

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);

  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  const handleUploadSave = useCallback((newUploads: FleetDocumentUpload[]) => {
    setUploads((prev) => [...prev, ...newUploads]);
  }, []);

  const handleUploadDelete = useCallback((id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);

  // Obrazac je jednokorakan; footer i dalje očekuje navigaciju pa ostaje na istom koraku.
  const handleStepChange = useCallback(() => {
    setStepCurrent(0);
  }, []);

  const steps = useMemo(
    () => [
      <WizardStep key="details" stepNumber={0} stepCurrent={stepCurrent}>
        <div className="flex justify-center px-4 pt-8 pb-6">
          <div className="w-full max-w-2xl">
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
              Ostale informacije
            </h1>
            <p className="text-center text-md text-slate-500 mb-6">
              Naziv, iznos i datumi troška, uz račun ako ga imate.
            </p>
            <form noValidate className="text-left">
              <div className="flex flex-col gap-3 mb-3">
                <div className="flex-1">
                  <ExpenseTypeDropdown
                    name="expenseTypeId"
                    label={t("admin.fleet.expense.add.type")}
                    value={formValues.expenseTypeId}
                    onChange={handleDropdownChange}
                    appliesTo={ExpenseAppliesTo.VEHICLE}
                    error={errors.expenseTypeId}
                    required
                  />
                </div>
                <div className="flex-1">
                  <TextField
                    type="text"
                    name="name"
                    label={t("admin.fleet.expense.add.name")}
                    placeholder={t("admin.fleet.expense.add.name.placeholder")}
                    onChange={handleTextFieldChange}
                    error={errors.name}
                    value={formValues.name}
                    required
                    endIcon=""
                  />
                </div>
                <div className="flex-1">
                  <TextArea
                    name="description"
                    label={t("admin.fleet.expense.add.description")}
                    placeholder={t("admin.fleet.expense.add.description.placeholder")}
                    onChange={handleTextFieldChange}
                    error={errors.description}
                    value={formValues.description}
                    required
                    endIcon=""
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <DatePicker
                      name="expenseDate"
                      label={t("admin.fleet.expense.add.expensedate")}
                      placeholder={t("admin.fleet.expense.add.expensedate.placeholder")}
                      onChange={handleDateChange}
                      error={errors.expenseDate}
                      value={formValues.expenseDate}
                      width="full"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <DatePicker
                      name="paymentDate"
                      label={t("admin.fleet.expense.add.paymentdate")}
                      placeholder={t("admin.fleet.expense.add.paymentdate.placeholder")}
                      onChange={handleDateChange}
                      error={errors.paymentDate}
                      value={formValues.paymentDate}
                      width="full"
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <NumberField
                      label={t("admin.fleet.expense.add.amount")}
                      name="amount"
                      onChange={handleAmountChange}
                      value={formValues.amount}
                      error={errors.amount}
                      decimals={2}
                      groupAppend="€"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <NumberField
                      label={t("admin.fleet.expense.add.taxamount")}
                      name="taxAmount"
                      onChange={handleTaxAmountChange}
                      value={formValues.taxAmount}
                      error={errors.taxAmount}
                      decimals={2}
                      groupAppend="€"
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <FileUploadControl
                    uploads={uploads}
                    onUploadSave={handleUploadSave}
                    onUploadDelete={handleUploadDelete}
                    buttonText={t("admin.fleet.expense.add.upload")}
                    modalTitle="Upload dokumenta"
                    emptyText="Dodajte datoteke"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </WizardStep>,
    ],
    [
      stepCurrent,
      t,
      isFullScreen,
      formValues,
      errors,
      uploads,
      handleDropdownChange,
      handleTextFieldChange,
      handleDateChange,
      handleAmountChange,
      handleTaxAmountChange,
      handleUploadSave,
      handleUploadDelete,
    ]
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      position={isFullScreen ? "side" : "centered"}
      size={isFullScreen ? "full" : "medium"}
      intro={
        isFullScreen
          ? {
              content: (
                <ModalIntro
                  animation={invoiceAnimation}
                  title={
                    expenseId
                      ? "Uredite trošak vozila!"
                      : "Zabilježite trošak u par klikova!"
                  }
                  description="Odaberite vrstu troška, upišite iznos i priložite račun. Trošak odmah ulazi u pregled troškova vozila."
                  show={show}
                />
              ),
            }
          : undefined
      }
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={
          expenseId
            ? FLEET_VEHICLE_EXPENSE_EDIT_PAGE_TITLE.text
            : FLEET_VEHICLE_EXPENSE_CREATE_PAGE_TITLE.text
        }
        onHide={onClose}
      />
      <ModalBody>
        {/* U manjem prozoru sadržaj inače dodiruje rubove, a koraci su različite visine pa
            prozor skače — dodatni razmak i najmanja visina drže ga mirnim. */}
        <div className={isFullScreen ? undefined : "px-6 pb-2 min-h-[460px]"}>
          <Wizard steps={steps} />
        </div>
        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleStepChange}
        onBackStep={handleStepChange}
        onFinish={handleFormSubmit}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default memo(VehicleExpenseEditCard);
